/**
 * Encryption toggle button shown in the nav bar.
 * Clicking it enables/disables E2EE for outgoing 1:1 messages.
 */

import { Button } from "@/components/ui/button";
import { useEncryption } from "@/hooks/useEncryption";
import { Shield, ShieldCheck, ShieldAlert, ShieldOff } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  toggleEncryption,
  toggleGroupEncryption,
  selectEncryptionEnabled,
  selectGroupEncryptionEnabled,
  selectConversations,
  selectCurrConversation,
  selectConversationEncryptionReady,
} from "@/features/control/controlSlice";
import { selectRouterE2eeEnabled } from "@/features/auth/authSlice";

const EncryptionSettings = () => {
  const dispatch = useAppDispatch();
  const { status } = useEncryption();
  const encryptionEnabled = useAppSelector(selectEncryptionEnabled);
  const groupEncryptionEnabled = useAppSelector(selectGroupEncryptionEnabled);
  const routerE2eeEnabled = useAppSelector(selectRouterE2eeEnabled);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations = useAppSelector(selectConversations);
  const currConv = currConversationIndex !== null ? conversations[currConversationIndex] : null;
  const isGroupConversation = currConv?.type === "Group";
  const conversationEncryptionReady = useAppSelector(selectConversationEncryptionReady);

  if (!routerE2eeEnabled) {
    return (
      <Button variant="ghost" size="sm" className="gap-2" disabled title="Encryption Unavailable">
        <Shield className="h-5 w-5 text-neutral-400" />
        <span className="hidden md:inline">Encryption Unavailable</span>
      </Button>
    );
  }

  // Group conversations: show a toggleable button once the key is ready.
  // While the key is still being distributed, show a non-interactive waiting state.
  if (isGroupConversation) {
    if (!conversationEncryptionReady) {
      return (
        <Button variant="ghost" size="sm" className="gap-2" disabled title="Awaiting Group Key">
          <ShieldAlert className="h-5 w-5 text-yellow-500" />
          <span className="hidden md:inline">Awaiting Group Key</span>
        </Button>
      );
    }
    const icon = groupEncryptionEnabled
      ? <ShieldCheck className="h-5 w-5 text-green-500" />
      : <ShieldOff className="h-5 w-5 text-neutral-400" />;
    const label = groupEncryptionEnabled ? "Group Encrypted" : "Group Encryption Off";
    return (
      <Button variant="ghost" size="sm" className="gap-2" onClick={() => dispatch(toggleGroupEncryption())} title={label}>
        {icon}
        <span className="hidden md:inline">{label}</span>
      </Button>
    );
  }

  const getIcon = () => {
    if (status.isLoading) {
      return <Shield className="h-5 w-5 animate-pulse" />;
    }
    if (!status.isInitialized) {
      return <ShieldAlert className="h-5 w-5 text-yellow-500" />;
    }
    if (!encryptionEnabled) {
      return <ShieldOff className="h-5 w-5 text-neutral-400" />;
    }
    return <ShieldCheck className="h-5 w-5 text-green-500" />;
  };

  const getLabel = () => {
    if (status.isLoading) return "Initializing...";
    if (!status.isInitialized) return status.error ? "Encryption Error" : "Encryption Inactive";
    return encryptionEnabled ? "Encryption On" : "Encryption Off";
  };

  const handleClick = () => {
    if (!status.isInitialized) return;
    dispatch(toggleEncryption());
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="gap-2"
      onClick={handleClick}
      disabled={status.isLoading || !status.isInitialized}
      title={getLabel()}
    >
      {getIcon()}
      <span className="hidden md:inline">{getLabel()}</span>
    </Button>
  );
};

export default EncryptionSettings;
